import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import { z } from 'zod';
import { createApiResponseSchema } from '../../../utils';
import { partnerApiPath } from '../../apiPath';
import { PartnerCuratorSchema } from '../../curators/getPartnerCurators.api';
import { partnerHttpClient } from '../../httpClient';
import { LocalDateTimeSchema } from './_shared/LocalDateTimeSchema';

const GetCampaignSeedingApplicantsRequestSchema = z
  .object({
    campaignId: z.union([z.number(), z.string()]),
    nickname: z.string().optional(),
    gender: z.enum(['M', 'F']).optional(),
    ageRange: z.string().optional(),
    customerGender: z.enum(['M', 'F']).optional(),
    customerAgeRange: z.string().optional(),
    categoryCode: z.string().optional(),
    snsType: z.enum(['INSTAGRAM', 'YOUTUBE', 'TIKTOK', 'TWITTER', 'ETC']).optional(),
    likedBrand: z.boolean().optional(),
    likedByBrand: z.boolean().optional(),
    brandSoldHistory: z.boolean().optional(),
    sort: z
      .enum(['LAST_SALES_AT_DESC', 'MONTHLY_AVERAGE_SALES_DESC', 'MAX_SALES_PER_GOODS_DESC'])
      .optional(),
    page: z.number().int().min(1).optional(),
    size: z.number().int().min(1).max(100).optional(),
  })
  .describe('GetCampaignSeedingApplicantsRequestSchema');

export type GetCampaignSeedingApplicantsRequest = z.infer<
  typeof GetCampaignSeedingApplicantsRequestSchema
>;

const SeedingApplicantSchema = z
  .object({
    participationId: z.number(),
    curator: PartnerCuratorSchema,
    selectedContentType: z.string().nullable().optional(),
    messageToBrand: z.string().nullable().optional(),
    appliedAt: LocalDateTimeSchema,
  })
  .describe('SeedingApplicantSchema');

export type SeedingApplicant = z.infer<typeof SeedingApplicantSchema>;

const GetCampaignSeedingApplicantsResponseDataSchema = z
  .object({
    list: z.array(SeedingApplicantSchema),
  })
  .describe('GetCampaignSeedingApplicantsResponseDataSchema');

const GetCampaignSeedingApplicantsResponseSchema = createApiResponseSchema(
  GetCampaignSeedingApplicantsResponseDataSchema,
);

export type GetCampaignSeedingApplicantsResponse = z.infer<
  typeof GetCampaignSeedingApplicantsResponseSchema
>;

export const getCampaignSeedingApplicants = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetCampaignSeedingApplicantsRequestSchema),
    async ({ campaignId, ...query }: GetCampaignSeedingApplicantsRequest) =>
      executeStandardRequest(async () =>
        partnerHttpClient.get<GetCampaignSeedingApplicantsResponse>(
          partnerApiPath.getCampaignSeedingApplicants(campaignId),
          query,
        ),
      ),
    validateApiResponse(GetCampaignSeedingApplicantsResponseSchema),
  ),
);
